import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, Crown, Shield, User, LogOut, Pencil, Trash2, Settings, Check, X, Bot } from "lucide-react";
import { TelegramSettings } from "@/components/TelegramSettings";
import { useNavigate } from "react-router-dom";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { cn } from "@/lib/utils";
import { sanitizeText } from "@/lib/sanitize";
import { getCurrentUser } from "@/lib/authUser";

interface GroupMember {
  id: string;
  user_id: string;
  role: string;
  joined_at: string;
  user_profile?: {
    full_name: string | null;
  } | null;
}

interface GroupSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupId: string;
  groupName: string;
  onGroupUpdated: () => void;
}

const GroupSettingsDialog = ({
  open,
  onOpenChange,
  groupId,
  groupName,
  onGroupUpdated,
}: GroupSettingsDialogProps) => {
  
  const navigate = useNavigate();
  const { setWorkspace } = useWorkspace();
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string>("");
  const [groupCreatorId, setGroupCreatorId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [newGroupName, setNewGroupName] = useState(groupName);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedMemberForTransfer, setSelectedMemberForTransfer] = useState<GroupMember | null>(null);

  useEffect(() => {
    if (open) {
      fetchGroupData();
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && open) {
        fetchGroupData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [open, groupId]);

  const fetchGroupData = async () => {
    try {
      setLoading(true);
      const user = await getCurrentUser();
      if (!user) return;
      
      setCurrentUserId(user.id);

      const { data: groupData } = await supabase
        .from("groups")
        .select("created_by")
        .eq("id", groupId)
        .single();
      
      if (groupData) {
        setGroupCreatorId(groupData.created_by);
      }

      const { data: membersData, error } = await supabase
        .from("group_members")
        .select("id, user_id, role, joined_at")
        .eq("group_id", groupId)
        .order("joined_at", { ascending: true });

      if (error) throw error;

      if (membersData && membersData.length > 0) {
        // Use the security-definer function to fetch display names for all group members
        // (direct profiles query is restricted to own row only by RLS)
        const { data: displayNames } = await supabase
          .rpc("get_group_member_display_names", { p_group_id: groupId });

        const enrichedMembers = membersData.map(member => ({
          ...member,
          user_profile: {
            full_name: displayNames?.find((d: any) => d.user_id === member.user_id)?.display_name || null
          }
        }));

        setMembers(enrichedMembers);
      } else {
        setMembers([]);
      }
    } catch (error: any) {
      console.error("Error fetching group data:", error);
      toast.error("Error", { description: "Failed to load group data" });
    } finally {
      setLoading(false);
    }
  };

  const isCreator = currentUserId === groupCreatorId;
  const currentUserRole = members.find(m => m.user_id === currentUserId)?.role;
  const isAdmin = currentUserRole === 'admin';
  const canManageRoles = isCreator;

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'creator':
        return <Crown className="h-3.5 w-3.5 text-yellow-500" />;
      case 'admin':
        return <Shield className="h-3.5 w-3.5 text-blue-500" />;
      default:
        return <User className="h-3.5 w-3.5 text-muted-foreground" />;
    }
  };

  const handleRoleChange = async (memberId: string, newRole: string) => {
    try {
      const { error } = await supabase
        .from("group_members")
        .update({ role: newRole })
        .eq("id", memberId);

      if (error) throw error;

      toast.success("Role updated", { description: "Member role has been updated successfully." });
      fetchGroupData();
    } catch (error: any) {
      toast.error("Error", { description: error.message });
    }
  };

  const handleLeaveGroup = async () => {
    try {
      if (isCreator) {
        toast.error("Cannot leave", { description: "Group creators cannot leave. Delete or transfer ownership first." });
        return;
      }

      const { error } = await supabase
        .from("group_members")
        .delete()
        .eq("group_id", groupId)
        .eq("user_id", currentUserId);

      if (error) throw error;

      toast.success("Left group", { description: "You have left the group successfully." });
      
      setWorkspace("personal");
      navigate("/dashboard");
      onOpenChange(false);
    } catch (error: any) {
      toast.error("Error", { description: error.message });
    }
  };

  const handleUpdateGroupName = async () => {
    if (!newGroupName.trim()) {
      toast.error("Error", { description: "Group name cannot be empty" });
      return;
    }

    try {
      const sanitizedName = sanitizeText(newGroupName, 100);
      const { error } = await supabase
        .from("groups")
        .update({ name: sanitizedName })
        .eq("id", groupId);

      if (error) throw error;

      toast.success("Group updated", { description: "Group name has been updated successfully." });
      setEditingName(false);
      onGroupUpdated();
    } catch (error: any) {
      toast.error("Error", { description: error.message });
    }
  };

  const handleDeleteGroup = async () => {
    try {
      if (!isCreator) {
        toast.error("Permission denied", { description: "Only the group creator can delete the group." });
        return;
      }

      const { error } = await supabase
        .from("groups")
        .delete()
        .eq("id", groupId)
        .eq("created_by", currentUserId);

      if (error) throw error;

      toast.success("Group deleted", { description: "The group has been deleted successfully." });
      
      setWorkspace("personal");
      navigate("/dashboard/personal/hot");
      onOpenChange(false);
    } catch (error: any) {
      toast.error("Error", { description: error.message });
    }
  };

  const handleTransferLeadership = async () => {
    if (!selectedMemberForTransfer) return;
    
    try {
      if (!isCreator) {
        toast.error("Permission denied", { description: "Only the group leader can transfer leadership." });
        return;
      }

      const { error: groupError } = await supabase
        .from("groups")
        .update({ created_by: selectedMemberForTransfer.user_id })
        .eq("id", groupId)
        .eq("created_by", currentUserId);

      if (groupError) throw groupError;

      const { error: roleError } = await supabase
        .from("group_members")
        .update({ role: "leader" })
        .eq("id", selectedMemberForTransfer.id);

      if (roleError) throw roleError;

      const currentUserMember = members.find(m => m.user_id === currentUserId);
      if (currentUserMember) {
        const { error: oldLeaderError } = await supabase
          .from("group_members")
          .update({ role: "admin" })
          .eq("id", currentUserMember.id);

        if (oldLeaderError) throw oldLeaderError;
      }

      toast.success("Leadership transferred", { description: `${selectedMemberForTransfer.user_profile?.full_name || "User"} is now the group leader.` });
      
      setTransferDialogOpen(false);
      setSelectedMemberForTransfer(null);
      fetchGroupData();
      onGroupUpdated();
    } catch (error: any) {
      toast.error("Error", { description: error.message });
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-4 pt-4 pb-2">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Settings className="h-4 w-4" />
              Group Settings
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="general" className="w-full">
            <TabsList className="w-full justify-start rounded-none border-b bg-transparent px-4 h-9">
              <TabsTrigger 
                value="general" 
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-3 py-1.5 text-sm"
              >
                General
              </TabsTrigger>
              <TabsTrigger 
                value="members" 
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-3 py-1.5 text-sm"
              >
                Members ({members.length})
              </TabsTrigger>
              <TabsTrigger 
                value="telegram" 
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-3 py-1.5 text-sm"
              >
                <Bot className="h-3.5 w-3.5 mr-1" />
                Telegram
              </TabsTrigger>
              <TabsTrigger 
                value="danger" 
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-destructive data-[state=active]:bg-transparent data-[state=active]:text-destructive px-3 py-1.5 text-sm"
              >
                Danger
              </TabsTrigger>
            </TabsList>

            {/* General Tab */}
            <TabsContent value="general" className="px-4 py-3 mt-0 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Group Name</Label>
                {editingName ? (
                  <div className="flex gap-1.5">
                    <Input
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      placeholder="Enter group name"
                      className="h-8 text-sm"
                    />
                    <Button onClick={handleUpdateGroupName} size="icon" className="h-8 w-8 shrink-0">
                      <Check className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() => {
                        setEditingName(false);
                        setNewGroupName(groupName);
                      }}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-muted/50 rounded-md px-3 py-2">
                    <span className="font-medium text-sm">{groupName}</span>
                    {isCreator && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => setEditingName(true)}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Your Role</Label>
                <div className="flex items-center gap-2 bg-muted/50 rounded-md px-3 py-2">
                  {getRoleIcon(isCreator ? 'creator' : currentUserRole || 'member')}
                  <span className="text-sm font-medium capitalize">
                    {isCreator ? 'Creator' : currentUserRole || 'Member'}
                  </span>
                </div>
              </div>
            </TabsContent>

            {/* Members Tab */}
            <TabsContent value="members" className="mt-0">
              <ScrollArea className="h-[280px]">
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    Loading members...
                  </div>
                ) : members.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    No members found
                  </div>
                ) : (
                  <div className="divide-y">
                    {members.map((member) => {
                      const isCurrentUser = member.user_id === currentUserId;
                      const isMemberCreator = member.user_id === groupCreatorId;
                      const memberName = member.user_profile?.full_name || "Unknown User";
                      
                      return (
                        <div
                          key={member.id}
                          className={cn(
                            "flex items-center justify-between px-4 py-2.5 hover:bg-muted/50 transition-colors",
                            isCurrentUser && "bg-muted/30"
                          )}
                        >
                          <div className="flex items-center gap-2.5 min-w-0 flex-1">
                            <div className="flex items-center justify-center w-7 h-7 rounded-full bg-muted shrink-0">
                              {getRoleIcon(isMemberCreator ? 'creator' : member.role)}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium truncate">
                                {memberName}
                                {isCurrentUser && (
                                  <span className="text-muted-foreground text-xs ml-1">(You)</span>
                                )}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                Joined {new Date(member.joined_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-1.5 shrink-0">
                            {isCreator && !isMemberCreator && !isCurrentUser && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => {
                                  setSelectedMemberForTransfer(member);
                                  setTransferDialogOpen(true);
                                }}
                                title="Transfer leadership"
                              >
                                <Crown className="h-3.5 w-3.5 text-yellow-500" />
                              </Button>
                            )}

                            {!isMemberCreator && canManageRoles && !isCurrentUser ? (
                              <Select
                                value={member.role}
                                onValueChange={(value) => handleRoleChange(member.id, value)}
                              >
                                <SelectTrigger className="h-7 w-24 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="member" className="text-xs">Member</SelectItem>
                                  <SelectItem value="admin" className="text-xs">Admin</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="text-xs text-muted-foreground px-2 capitalize">
                                {isMemberCreator ? 'Creator' : member.role}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            {/* Telegram Tab */}
            <TabsContent value="telegram" className="px-4 py-3 mt-0">
              <TelegramSettings groupId={groupId} isAdmin={isCreator || isAdmin} />
            </TabsContent>

            {/* Danger Tab */}
            <TabsContent value="danger" className="px-4 py-3 mt-0 space-y-3">
              <div className="rounded-md border border-destructive/20 bg-destructive/5 p-3">
                <p className="text-xs text-muted-foreground mb-3">
                  {isCreator 
                    ? "As the creator, you can delete this group. This action is permanent."
                    : "Leave this group if you no longer want to be a member."}
                </p>
                {isCreator ? (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setDeleteDialogOpen(true)}
                    className="w-full gap-2"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete Group
                  </Button>
                ) : (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setLeaveDialogOpen(true)}
                    className="w-full gap-2"
                  >
                    <LogOut className="h-3.5 w-3.5" />
                    Leave Group
                  </Button>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Leave Group Confirmation */}
      <AlertDialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave Group?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to leave this group? You'll need a new invite link to rejoin.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleLeaveGroup}>Leave Group</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Group Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Group?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all members, leads, invites, and settings.
              <strong className="block mt-2 text-destructive">This action cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteGroup}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Group
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transfer Leadership Confirmation */}
      <AlertDialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Transfer Leadership?</AlertDialogTitle>
            <AlertDialogDescription>
              Transfer to <strong>{selectedMemberForTransfer?.user_profile?.full_name || "this member"}</strong>?
              They will have full control and you will become an admin.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedMemberForTransfer(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleTransferLeadership}>Transfer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default GroupSettingsDialog;
